import { useState, useEffect } from 'react';
import { refreshUserData, updateUserData, triggerGlobalUserRefresh } from '../utils/userUtils';
import { getApiBaseUrl } from '../utils/apiConfig';

// API Configuration
const API_BASE_URL = getApiBaseUrl();

const AdminDashboard = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [expiringSubscriptions, setExpiringSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [userToExtend, setUserToExtend] = useState(null);
  const [extensionDays, setExtensionDays] = useState(30);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationData, setNotificationData] = useState({ type: 'success', title: '', message: '' });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordResetData, setPasswordResetData] = useState({ userId: null, username: '', newPassword: '' });
  const [showResetPassword, setShowResetPassword] = useState(false);

  const showNotification = (type, title, message) => {
    setNotificationData({ type, title, message });
    setShowNotificationModal(true);
  };

  useEffect(() => {
    console.log('AdminDashboard received user:', user); // Debug log
    if (user && user.is_admin) {
      fetchAdminData();
    }
  }, [user]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      const headers = {
        'Authorization': `Bearer ${user.token}`,
        'Content-Type': 'application/json'
      };
      
      // Fetch all users with subscription info
      const usersResponse = await fetch(`${API_BASE_URL}/api/admin/users`, { headers });
      if (usersResponse.ok) {
        const data = await usersResponse.json();
        setUsers(data.users || []);
        
        // Extract subscriptions from users data
        const allSubscriptions = data.users
          .filter(user => user.subscription)
          .map(user => ({
            ...user.subscription,
            username: user.username,
            email: user.email,
            user_id: user._id
          }));
        setSubscriptions(allSubscriptions);
        
        // Extract expiring subscriptions (next 7 days)
        const now = new Date();
        const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        const expiring = allSubscriptions.filter(sub => {
          if (!sub.end_date || sub.status !== 'active' && sub.status !== 1) return false;
          const endDate = new Date(sub.end_date);
          return endDate <= sevenDaysFromNow && endDate > now;
        });
        setExpiringSubscriptions(expiring);
      }

      // Fetch pending payments
      const paymentsResponse = await fetch(`${API_BASE_URL}/api/admin/payments?status=pending`, { headers });
      if (paymentsResponse.ok) {
        const data = await paymentsResponse.json();
        setPendingPayments(data.payments || []);
      } else {
        console.error('Failed to fetch pending payments:', paymentsResponse.status);
        setPendingPayments([]);
      }

      // Fetch subscription plans
      const plansResponse = await fetch(`${API_BASE_URL}/api/admin/plans`, { headers });
      if (plansResponse.ok) {
        const data = await plansResponse.json();
        setSubscriptionPlans(data.plans || []);
      }

      // Fetch stats
      const statsResponse = await fetch(`${API_BASE_URL}/api/admin/stats`, { headers });
      if (statsResponse.ok) {
        const data = await statsResponse.json();
        setAnalytics(data.stats || {});
      }

    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get auth headers
  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${user.token}`
  });

  const updateUserRole = async (userId, newRole) => {
    console.log('Attempting role update with admin user:', user);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        fetchAdminData();
        showNotification('success', 'Success', 'User role updated successfully!');
      } else {
        const errorData = await response.json();
        console.error('Role update failed:', errorData);
        showNotification('error', 'Error', 'Failed to update user role: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      showNotification('error', 'Error', 'Error updating user role');
    }
  };

  const extendSubscription = (userId, username) => {
    setUserToExtend({ _id: userId, username });
    setExtensionDays(30); // Default to 30 days
    setShowExtendModal(true);
  };

  const confirmExtendSubscription = async () => {
    if (!userToExtend || extensionDays <= 0) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/subscription/extend`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_id: userToExtend._id,
          additional_days: extensionDays
        })
      });

      if (response.ok) {
        fetchAdminData(); // Refresh admin data
        
        // Trigger global user refresh for the extended user
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id === userToExtend._id) {
          // Use global refresh system
          await triggerGlobalUserRefresh(userToExtend._id);
          console.log('✅ Global user refresh triggered for subscription extension');
        }
        
        showNotification('success', 'Subscription Extended Successfully!', 
          `${userToExtend.username}'s subscription has been extended by ${extensionDays} days.${
            currentUser.id === userToExtend._id ? ' Your dashboard access has been updated automatically!' : ''
          }`
        );
        setShowExtendModal(false);
        setUserToExtend(null);
      } else {
        const errorData = await response.json();
        console.error('Extend subscription failed:', errorData);
        showNotification('error', 'Error', 'Failed to extend subscription: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error extending subscription:', error);
      showNotification('error', 'Error', 'Error extending subscription');
    }
  };

  const cancelExtendSubscription = () => {
    setShowExtendModal(false);
    setUserToExtend(null);
    setExtensionDays(30);
  };

  const cancelSubscription = async (userId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/subscription/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_id: userId
        })
      });

      if (response.ok) {
        fetchAdminData();
        
        // Trigger global user refresh for the cancelled user
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id === userId) {
          await triggerGlobalUserRefresh(userId);
          console.log('✅ Global user refresh triggered for subscription cancellation');
        }
        
        showNotification('success', 'Success', `Subscription cancelled successfully!${
          currentUser.id === userId ? ' Your access has been updated automatically.' : ''
        }`);
      } else {
        const errorData = await response.json();
        showNotification('error', 'Error', 'Failed to cancel subscription: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      showNotification('error', 'Error', 'Error cancelling subscription');
    }
  };

  const approvePayment = async (paymentId, userId, planName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/payment/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          payment_id: paymentId,
          user_id: userId,
          plan_type: planName || 'premium',
          duration_days: planName === 'trial' ? 4 : 
                        planName === 'quarterly' ? 90 :
                        planName === 'halfyearly' ? 180 : 
                        planName === 'yearly' ? 365 : 30
        })
      });

      if (response.ok) {
        // Force refresh admin data
        await fetchAdminData();
        
        // Trigger global user refresh for the approved user
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id === userId) {
          await triggerGlobalUserRefresh(userId);
          console.log('✅ Global user refresh triggered for payment approval');
        }
        
        showNotification('success', 'Success', `Payment approved and subscription activated!${
          currentUser.id === userId ? ' Your access has been updated automatically.' : ''
        }`);
      } else {
        const errorData = await response.json();
        showNotification('error', 'Error', 'Failed to approve payment: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error approving payment:', error);
      showNotification('error', 'Error', 'Error approving payment');
    }
  };

  const handleRejectPayment = (payment) => {
    setSelectedPayment(payment);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const confirmRejectPayment = async () => {
    if (!selectedPayment || !rejectionReason.trim()) {
      showNotification('warning', 'Validation Error', 'Please provide a reason for rejection');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/payment/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          payment_id: selectedPayment._id,
          reason: rejectionReason.trim()
        })
      });

      if (response.ok) {
        // Force refresh admin data
        await fetchAdminData();
        setShowRejectModal(false);
        setSelectedPayment(null);
        setRejectionReason('');
        showNotification('success', 'Success', `Payment rejected successfully. User ${selectedPayment.user_id?.username?.split('@')[0] || 'User'} will be notified.`);
      } else {
        const errorData = await response.json();
        showNotification('error', 'Error', 'Failed to reject payment: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error rejecting payment:', error);
      showNotification('error', 'Error', 'Error rejecting payment');
    }
  };

  const resetPassword = async (userId, username) => {
    setPasswordResetData({ userId, username, newPassword: '' });
    setShowPasswordModal(true);
  };

  const confirmPasswordReset = async () => {
    const { userId, username, newPassword } = passwordResetData;
    
    if (!newPassword) {
      showNotification('warning', 'Validation Error', 'Please enter a new password');
      return;
    }
    
    if (newPassword.length < 6) {
      showNotification('warning', 'Validation Error', 'Password must be at least 6 characters long');
      return;
    }

    console.log('Attempting password reset with admin user:', user); // Debug log
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_id: userId,
          new_password: newPassword
        })
      });

      if (response.ok) {
        showNotification('success', 'Success', `Password reset successfully for ${username}!`);
        setShowPasswordModal(false);
        setPasswordResetData({ userId: null, username: '', newPassword: '' });
        setShowResetPassword(false);
      } else {
        const errorData = await response.json();
        console.error('Password reset failed:', errorData);
        showNotification('error', 'Error', 'Failed to reset password: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      showNotification('error', 'Error', 'Error resetting password');
    }
  };

  const cancelPasswordReset = () => {
    setShowPasswordModal(false);
    setPasswordResetData({ userId: null, username: '', newPassword: '' });
    setShowResetPassword(false);
  };

  const deleteUser = async (userId, username, email) => {
    // Show confirmation modal
    setUserToDelete({ _id: userId, username, email });
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userToDelete._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        showNotification('success', 'Success', `User ${userToDelete.username} deleted successfully!`);
        
        // Remove user from the local state
        setUsers(users.filter(u => u._id !== userToDelete._id));
        
        // Close modal
        setShowDeleteModal(false);
        setUserToDelete(null);
      } else {
        const errorData = await response.json();
        console.error('Delete user failed:', errorData);
        showNotification('error', 'Error', 'Failed to delete user: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('error', 'Error', 'Error deleting user');
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const createSubscriptionPlan = async (planData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/plans`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(planData)
      });

      if (response.ok) {
        fetchAdminData();
        showNotification('success', 'Success', 'Subscription plan created successfully!');
        setShowPlanModal(false);
        setEditingPlan(null);
      } else {
        const errorData = await response.json();
        showNotification('error', 'Error', 'Failed to create subscription plan: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating subscription plan:', error);
      showNotification('error', 'Error', 'Error creating subscription plan');
    }
  };

  const updateSubscriptionPlan = async (planId, planData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/plans/${planId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(planData)
      });

      if (response.ok) {
        fetchAdminData();
        showNotification('success', 'Success', 'Subscription plan updated successfully!');
        setShowPlanModal(false);
        setEditingPlan(null);
      } else {
        const errorData = await response.json();
        showNotification('error', 'Error', 'Failed to update subscription plan: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      showNotification('error', 'Error', 'Error updating subscription plan');
    }
  };

  const deleteSubscriptionPlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/plans/${planId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchAdminData();
        showNotification('success', 'Success', 'Subscription plan deleted successfully!');
      } else {
        const errorData = await response.json();
        showNotification('error', 'Error', 'Failed to delete subscription plan: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting subscription plan:', error);
      showNotification('error', 'Error', 'Error deleting subscription plan');
    }
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'text-green-600';
      case 2: return 'text-red-600';
      case 3: return 'text-gray-600';
      default: return 'text-yellow-600';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 3: return 'bg-purple-100 text-purple-800'; // Admin
      case 2: return 'bg-blue-100 text-blue-800';     // Premium
      case 1: return 'bg-gray-100 text-gray-800';     // Free
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 3: return 'Admin';
      case 2: return 'Premium';
      case 1: return 'Free';
      default: return 'Unknown';
    }
  };

  const getRoleText = (role) => {
    switch (role) {
      case 0: return 'Guest';
      case 1: return 'Free';
      case 2: return 'Premium';
      case 3: return 'Admin';
      default: return 'Unknown';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0: return 'Inactive';
      case 1: return 'Active';
      case 2: return 'Expired';
      case 3: return 'Cancelled';
      default: return 'Unknown';
    }
  };

  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              {/* Navigation Tabs in Header */}
              <nav className="mt-4 flex flex-wrap space-x-8">
                {['users', 'subscriptions', 'payments', 'plans', 'analytics', 'expiring'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab}
                    {tab === 'payments' && pendingPayments.length > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                        {pendingPayments.length}
                      </span>
                    )}
                    {tab === 'expiring' && expiringSubscriptions.length > 0 && (
                      <span className="ml-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                        {expiringSubscriptions.length}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {user.username?.split('@')[0]?.charAt(0).toUpperCase() + user.username?.split('@')[0]?.slice(1) || 'User'}</span>
              <button
                onClick={onLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tab Content */}
        <div>
          {activeTab === 'users' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">User Management</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((userData) => (
                      <tr key={userData._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{userData.username}</div>
                            <div className="text-sm text-gray-500">{userData.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={userData.role}
                            onChange={(e) => updateUserRole(userData._id, parseInt(e.target.value))}
                            className="text-sm border rounded px-2 py-1"
                          >
                            <option value={0}>Guest</option>
                            <option value={1}>Free</option>
                            <option value={2}>Premium</option>
                            <option value={3}>Admin</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm text-gray-900">{userData.subscription?.plan_type || 'No Plan'}</div>
                            <div className={`text-sm ${getStatusColor(userData.subscription?.status)}`}>
                              {getStatusText(userData.subscription?.status)}
                            </div>
                            {userData.subscription?.end_date && (
                              <div className="text-xs text-gray-500">
                                Expires: {new Date(userData.subscription.end_date).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {userData.last_login ? new Date(userData.last_login).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-col space-y-1">
                            <div>
                              <button
                                onClick={() => extendSubscription(userData._id, userData.username)}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                Extend Sub
                              </button>
                              {userData.role < 3 && ( // Don't show cancel subscription for admin users
                                <button
                                  onClick={() => cancelSubscription(userData._id)}
                                  className="text-red-600 hover:text-red-900 mr-3"
                                >
                                  Cancel Sub
                                </button>
                              )}
                            </div>
                            <div>
                              <button
                                onClick={() => resetPassword(userData._id, userData.username)}
                                className="text-orange-600 hover:text-orange-900 mr-3"
                              >
                                Reset Password
                              </button>
                              {userData.role < 3 && ( // Don't show delete for admin users
                                <button
                                  onClick={() => deleteUser(userData._id, userData.username, userData.email)}
                                  className="text-red-800 hover:text-red-900 font-semibold"
                                >
                                  Delete User
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'subscriptions' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">All Subscriptions</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {subscriptions.length} active subscription{subscriptions.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Left</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscriptions.map((sub) => (
                      <tr key={sub._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{sub.username}</div>
                          <div className="text-sm text-gray-500">{sub.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {sub.plan_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            sub.status === 'active' || sub.status === 1 ? 'bg-green-100 text-green-800' :
                            sub.status === 'expired' || sub.status === 2 ? 'bg-red-100 text-red-800' :
                            sub.status === 'cancelled' || sub.status === 3 ? 'bg-gray-100 text-gray-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {getStatusText(sub.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div>Start: {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : 'N/A'}</div>
                          <div>End: {sub.end_date ? new Date(sub.end_date).toLocaleDateString() : 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getDaysRemaining(sub.end_date)} days
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => extendSubscription(sub.user_id, sub.username)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            Extend
                          </button>
                          <button
                            onClick={() => cancelSubscription(sub.user_id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {subscriptions.length === 0 && (
                  <div className="px-6 py-8 text-center">
                    <div className="text-gray-400 mb-2">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-medium text-gray-500">No active subscriptions</h3>
                    <p className="text-sm text-gray-400">No users have active subscriptions yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Pending Payment Requests</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {pendingPayments.length} payment{pendingPayments.length !== 1 ? 's' : ''} awaiting verification
                  </p>
                </div>
                {pendingPayments.length > 0 && (
                  <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                    {pendingPayments.length} New Request{pendingPayments.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Details</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan & Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Details</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pendingPayments.map((payment) => (
                      <tr key={payment._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                                <span className="text-white font-medium text-sm">
                                  {payment.user_id?.username?.split('@')[0]?.charAt(0).toUpperCase() || 'U'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {payment.user_id?.username?.split('@')[0]?.charAt(0).toUpperCase() + payment.user_id?.username?.split('@')[0]?.slice(1) || 'Unknown User'}
                              </div>
                              <div className="text-sm text-gray-500">{payment.user_id?.email || 'No email'}</div>
                              <div className="text-xs text-gray-400">User ID: {payment.user_id?._id || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 font-medium">{payment.plan_name}</div>
                          <div className="text-sm text-green-600 font-semibold">NRs. {payment.amount}</div>
                          <div className="text-xs text-gray-500">Plan ID: {payment.plan_id || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {payment.payment_details ? (
                            <div className="text-sm">
                              <div className="text-gray-900 font-medium">
                                {payment.payment_details.method === 'esewa' ? 'eSewa' : 
                                 payment.payment_details.method === 'khalti' ? 'Khalti' : 
                                 payment.payment_details.method === 'bank' ? 'Bank Transfer' : 
                                 payment.payment_details.method}
                              </div>
                              <div className="text-gray-600 text-xs">
                                {payment.payment_details.method === 'bank' ? 'Account: ' : 'Phone: '}
                                {payment.payment_details.phone_number}
                              </div>
                              <div className="text-gray-600 text-xs">
                                TXN ID: {payment.payment_details.transaction_id}
                              </div>
                              <div className="text-green-600 text-xs font-medium">
                                Paid: NRs. {payment.payment_details.amount_paid}
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-400">No payment details</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div>{new Date(payment.created_at).toLocaleDateString()}</div>
                          <div className="text-xs">{new Date(payment.created_at).toLocaleTimeString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-col space-y-2">
                            <button
                              onClick={() => approvePayment(payment._id, payment.user_id._id, payment.plan_name)}
                              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm font-medium"
                            >
                              ✓ Approve & Activate
                            </button>
                            <button
                              onClick={() => handleRejectPayment(payment)}
                              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm font-medium"
                            >
                              ✗ Reject Payment
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pendingPayments.length === 0 && (
                  <div className="px-6 py-8 text-center">
                    <div className="text-gray-400 mb-2">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-medium text-gray-500">No pending payments</h3>
                    <p className="text-sm text-gray-400">All payment requests have been processed.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'plans' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Subscription Plans</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {subscriptionPlans.length} plan{subscriptionPlans.length !== 1 ? 's' : ''} configured
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingPlan(null);
                    setShowPlanModal(true);
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Add New Plan
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan Details</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscriptionPlans.map((plan) => (
                      <tr key={plan._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{plan.name}</div>
                            <div className="text-sm text-gray-500">{plan.plan_type}</div>
                            {plan.description && (
                              <div className="text-xs text-gray-400 mt-1">{plan.description}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {plan.duration} {plan.duration_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {plan.currency} {plan.price}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            plan.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {plan.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              setEditingPlan(plan);
                              setShowPlanModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => updateSubscriptionPlan(plan._id, { ...plan, is_active: !plan.is_active })}
                            className={`mr-3 ${plan.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}`}
                          >
                            {plan.is_active ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            onClick={() => deleteSubscriptionPlan(plan._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {subscriptionPlans.length === 0 && (
                  <div className="px-6 py-8 text-center">
                    <div className="text-gray-400 mb-2">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-medium text-gray-500">No subscription plans</h3>
                    <p className="text-sm text-gray-400">Create your first subscription plan to get started.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'analytics' && analytics && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Total Users</h3>
                <p className="text-3xl font-bold text-blue-600">{analytics.total_users}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Active Subscriptions</h3>
                <p className="text-3xl font-bold text-green-600">{analytics.active_subscriptions}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Subscription Stats</h3>
                <div className="space-y-2">
                  {analytics?.subscription_stats?.map((stat, index) => (
                    <div key={index} className="text-sm">
                      <span className="font-medium">{stat.plan_type}:</span> {stat.plan_count}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'expiring' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Expiring Subscriptions (Next 7 Days)</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {expiringSubscriptions.length} subscription{expiringSubscriptions.length !== 1 ? 's' : ''} expiring soon
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Left</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {expiringSubscriptions.map((sub) => (
                      <tr key={sub._id} className="hover:bg-yellow-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{sub.username}</div>
                            <div className="text-sm text-gray-500">{sub.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {sub.plan_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                          {sub.end_date ? new Date(sub.end_date).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            getDaysRemaining(sub.end_date) <= 1 ? 'bg-red-100 text-red-800' :
                            getDaysRemaining(sub.end_date) <= 3 ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {getDaysRemaining(sub.end_date)} days
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => extendSubscription(sub.user_id, sub.username)}
                            className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 text-sm"
                          >
                            Extend Now
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {expiringSubscriptions.length === 0 && (
                  <div className="px-6 py-8 text-center">
                    <div className="text-gray-400 mb-2">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-medium text-gray-500">No subscriptions expiring soon</h3>
                    <p className="text-sm text-gray-400">All subscriptions are valid for more than 7 days.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Plan Modal */}
        {showPlanModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingPlan ? 'Edit Plan' : 'Create New Plan'}
                </h3>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const planData = {
                    name: formData.get('name'),
                    plan_type: formData.get('plan_type'),
                    duration: parseInt(formData.get('duration')),
                    duration_type: formData.get('duration_type'),
                    price: parseFloat(formData.get('price')),
                    currency: formData.get('currency'),
                    description: formData.get('description'),
                    is_active: formData.get('is_active') === 'on'
                  };
                  
                  if (editingPlan) {
                    updateSubscriptionPlan(editingPlan._id, planData);
                  } else {
                    createSubscriptionPlan(planData);
                  }
                }}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Plan Name</label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={editingPlan?.name || ''}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="e.g., Premium Monthly"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Plan Type</label>
                    <select
                      name="plan_type"
                      defaultValue={editingPlan?.plan_type || ''}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Select plan type</option>
                      <option value="trial">Trial</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="halfyearly">Half-Yearly</option>
                      <option value="yearly">Yearly</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Duration</label>
                    <input
                      type="number"
                      name="duration"
                      defaultValue={editingPlan?.duration || ''}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="e.g., 30"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Duration Type</label>
                    <select
                      name="duration_type"
                      defaultValue={editingPlan?.duration_type || 'days'}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="days">Days</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Price</label>
                    <input
                      type="number"
                      step="0.01"
                      name="price"
                      defaultValue={editingPlan?.price || ''}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="e.g., 1800"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Currency</label>
                    <select
                      name="currency"
                      defaultValue={editingPlan?.currency || 'NRs'}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="NRs">NRs (Nepali Rupees)</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      name="description"
                      defaultValue={editingPlan?.description || ''}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      rows="3"
                      placeholder="Brief description of the plan"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        defaultChecked={editingPlan?.is_active !== false}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Active</span>
                    </label>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPlanModal(false);
                        setEditingPlan(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                      {editingPlan ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Modal */}
        {showRejectModal && selectedPayment && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Reject Payment Request</h3>
                  <button
                    onClick={() => setShowRejectModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center mr-3">
                      <span className="text-white font-medium text-sm">
                        {selectedPayment.user_id?.username?.split('@')[0]?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {selectedPayment.user_id?.username?.split('@')[0]?.charAt(0).toUpperCase() + selectedPayment.user_id?.username?.split('@')[0]?.slice(1) || 'Unknown User'}
                      </div>
                      <div className="text-sm text-gray-500">{selectedPayment.user_id?.email || 'No email'}</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <div><strong>Plan:</strong> {selectedPayment.plan_name}</div>
                    <div><strong>Amount:</strong> NRs. {selectedPayment.amount}</div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a clear reason for rejecting this payment request..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This message will be sent to the user to explain why their payment was rejected.
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowRejectModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRejectPayment}
                    disabled={!rejectionReason.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reject Payment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Confirmation Modal */}
        {showDeleteModal && userToDelete && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Delete User</h3>
                  <button
                    onClick={cancelDeleteUser}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center">
                      <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <div>
                        <h4 className="font-semibold text-red-800">Warning: This action cannot be undone!</h4>
                        <p className="text-red-700 text-sm mt-1">
                          You are about to permanently delete this user and all their data.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-800 mb-2">User to be deleted:</h5>
                    <p className="text-gray-700 text-sm">
                      <strong>Username:</strong> {userToDelete.username}
                    </p>
                    <p className="text-gray-700 text-sm">
                      <strong>Email:</strong> {userToDelete.email}
                    </p>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                    <h5 className="font-medium text-yellow-800 mb-2">What will be deleted:</h5>
                    <ul className="text-yellow-700 text-sm space-y-1">
                      <li>• User account and profile</li>
                      <li>• All subscriptions and payment history</li>
                      <li>• All chatbot knowledge and interactions</li>
                      <li>• All user-generated content</li>
                    </ul>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelDeleteUser}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteUser}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                  >
                    Delete User Permanently
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Extend Subscription Modal */}
        {showExtendModal && userToExtend && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Extend Subscription</h3>
                  <button
                    onClick={cancelExtendSubscription}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-blue-800 mb-2">User: {userToExtend.username}</h4>
                    <p className="text-blue-700 text-sm">
                      Extend this user's subscription by adding additional days to their current plan.
                    </p>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Extension Period (Days) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="3650"
                      value={extensionDays}
                      onChange={(e) => setExtensionDays(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter number of days"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the number of days to extend the subscription (1-3650 days).
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-800 mb-2">Quick Options:</h5>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setExtensionDays(7)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200"
                      >
                        1 Week (7d)
                      </button>
                      <button
                        onClick={() => setExtensionDays(30)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200"
                      >
                        1 Month (30d)
                      </button>
                      <button
                        onClick={() => setExtensionDays(90)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200"
                      >
                        3 Months (90d)
                      </button>
                      <button
                        onClick={() => setExtensionDays(365)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200"
                      >
                        1 Year (365d)
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelExtendSubscription}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmExtendSubscription}
                    disabled={!extensionDays || extensionDays < 1}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Extend by {extensionDays} day{extensionDays !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Reset Password</h3>
                  <button
                    onClick={cancelPasswordReset}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-orange-800 mb-2">Reset Password for: {passwordResetData.username}</h4>
                    <p className="text-orange-700 text-sm">
                      Enter a new password for this user. The password must be at least 6 characters long.
                    </p>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showResetPassword ? "text" : "password"}
                        value={passwordResetData.newPassword}
                        onChange={(e) => setPasswordResetData({...passwordResetData, newPassword: e.target.value})}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Enter new password"
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                        className="absolute right-3 top-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        title={showResetPassword ? "Hide password" : "Show password"}
                      >
                        {showResetPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Password must be at least 6 characters long. Click the eye icon to show/hide password.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelPasswordReset}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmPasswordReset}
                    disabled={!passwordResetData.newPassword || passwordResetData.newPassword.length < 6}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reset Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification Modal */}
        {showNotificationModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
                      notificationData.type === 'success' ? 'bg-green-100' :
                      notificationData.type === 'error' ? 'bg-red-100' :
                      notificationData.type === 'warning' ? 'bg-yellow-100' :
                      'bg-blue-100'
                    }`}>
                      {notificationData.type === 'success' && (
                        <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {notificationData.type === 'error' && (
                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      {notificationData.type === 'warning' && (
                        <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      )}
                      {notificationData.type === 'info' && (
                        <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </div>
                    <div className="ml-3">
                      <h3 className={`text-lg font-medium ${
                        notificationData.type === 'success' ? 'text-green-900' :
                        notificationData.type === 'error' ? 'text-red-900' :
                        notificationData.type === 'warning' ? 'text-yellow-900' :
                        'text-blue-900'
                      }`}>
                        {notificationData.title}
                      </h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowNotificationModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-4">
                  <p className={`text-sm ${
                    notificationData.type === 'success' ? 'text-green-700' :
                    notificationData.type === 'error' ? 'text-red-700' :
                    notificationData.type === 'warning' ? 'text-yellow-700' :
                    'text-blue-700'
                  }`}>
                    {notificationData.message}
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowNotificationModal(false)}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      notificationData.type === 'success' ? 'bg-green-600 hover:bg-green-700' :
                      notificationData.type === 'error' ? 'bg-red-600 hover:bg-red-700' :
                      notificationData.type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
                      'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;